import React, { useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import DragIndicatorIcon from '../../ui/icons/DragIndicatorIcon'
import TrashIcon from '../../ui/icons/TrashIcon'
import Tooltip from '../../ui/Tooltip'
import StatusDot from './StatusDot'
import ColorPopover from './ColorPopover'
import DefaultBadge from './DefaultBadge'
import { MAX_NAME_LENGTH } from './constants'

const CARD_CLASS = 'tw:flex tw:flex-col tw:gap-1 tw:border tw:border-solid '
  + 'tw:rounded-lg! tw:px-3 tw:py-3 tw:bg-white'
const BORDER_CLASS = 'tw:border-gray-300'
const BORDER_ERROR_CLASS = 'tw:border-red-500'
const ROW_CLASS = 'tw:flex tw:items-center tw:gap-2'
const ERROR_CLASS = 'tw:m-0! tw:text-xs tw:text-red-600'
const HANDLE_CLASS = 'tw:text-gray-700 tw:cursor-grab tw:flex tw:items-center'
const DOT_BUTTON_CLASS = 'tw:bg-transparent tw:border-0 tw:p-0 tw:cursor-pointer tw:flex tw:items-center'
const NAME_CLASS = 'tw:flex-1 tw:border-0 tw:bg-transparent tw:p-0 tw:text-sm '
  + 'tw:text-gray-700 tw:outline-none'
const TRASH_BASE_CLASS = 'tw:bg-transparent tw:border-0 tw:p-0'
const TRASH_ACTIVE_CLASS = 'tw:cursor-pointer tw:text-gray-700 tw:hover:text-red-600'
const TRASH_DISABLED_CLASS = 'tw:cursor-not-allowed tw:text-gray-300'
const DEFAULT_TRASH_TOOLTIP = 'Vous ne pouvez pas supprimer le statut par défaut.'
const nameLengthError = name => {
  if (name.length <= MAX_NAME_LENGTH)
    return null
  return `Nom trop long (${name.length} / ${MAX_NAME_LENGTH} caractères)`
}

const StatusRow = ({
  row, colors, disabled, error, dragHandleProps, onNameChange, onColorChange, onDelete
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const handleName = useCallback(event => onNameChange(row.key, event.target.value), [onNameChange, row.key])
  const handleSelect = useCallback(token => onColorChange(row.key, token), [onColorChange, row.key])
  const handleDelete = useCallback(() => onDelete(row), [onDelete, row])
  const handleClose = useCallback(() => setIsPopoverOpen(false), [])

  const handleDotClick = useCallback(event => {
    setAnchorEl(event.currentTarget)
    setIsPopoverOpen(open => !open)
  }, [])

  const isTrashDisabled = disabled || row.isDefault
  const trashClass = `${TRASH_BASE_CLASS} ${isTrashDisabled ? TRASH_DISABLED_CLASS : TRASH_ACTIVE_CLASS}`

  const trashButton = (
    <button
      aria-label="Supprimer le statut"
      className={trashClass}
      disabled={isTrashDisabled}
      type="button"
      onClick={handleDelete}
    >
      <TrashIcon className="tw:size-4" />
    </button>
  )

  const nameError = nameLengthError(row.name) || error?.name?.[0]
  const cardClass = `${CARD_CLASS} ${nameError ? BORDER_ERROR_CLASS : BORDER_CLASS}`

  return (
    <div className={cardClass}>
      <div className={ROW_CLASS}>
        <span aria-label="Déplacer le statut" className={HANDLE_CLASS} {...dragHandleProps}>
          <DragIndicatorIcon className="tw:size-5" />
        </span>
        <button
          aria-label="Choisir la couleur"
          className={DOT_BUTTON_CLASS}
          disabled={disabled}
          type="button"
          onClick={handleDotClick}
        >
          <StatusDot color={row.color} />
        </button>
        <input
          className={NAME_CLASS}
          disabled={disabled}
          name={`issue-status-${row.key}`}
          placeholder="Nom du statut"
          value={row.name}
          onChange={handleName}
        />
        {row.isDefault ? <DefaultBadge /> : null}
        {row.isDefault ? (
          <Tooltip className="tw:flex tw:items-center" content={DEFAULT_TRASH_TOOLTIP}>
            {trashButton}
          </Tooltip>
        ) : trashButton}
      </div>
      {nameError ? <p className={ERROR_CLASS}>{nameError}</p> : null}
      <ColorPopover
        anchorEl={anchorEl}
        isOpen={isPopoverOpen}
        colors={colors}
        value={row.color}
        onClose={handleClose}
        onSelect={handleSelect}
      />
    </div>
  )
}

StatusRow.propTypes = {
  colors: PropTypes.arrayOf(PropTypes.string).isRequired,
  disabled: PropTypes.bool.isRequired,
  dragHandleProps: PropTypes.shape({}),
  error: PropTypes.shape({ name: PropTypes.arrayOf(PropTypes.string) }),
  onColorChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onNameChange: PropTypes.func.isRequired,
  row: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    isDefault: PropTypes.bool
  }).isRequired
}

StatusRow.defaultProps = {
  dragHandleProps: null,
  error: null
}

export default StatusRow
